import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { mockRfqManagementService } from '@/services/mock/MockRfqManagementService';
import { mockPrManagementService } from '@/services/mock/MockPrManagementService';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Layers, ArrowLeft } from 'lucide-react';
import { ROUTES } from '@/constants/routes';

export const RfqCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);

  const querySourcePrId = searchParams.get('sourcePrId');
  const querySourceRequestId = searchParams.get('sourceRequestId');
  const querySapPrNumber = searchParams.get('sapPrNumber');
  const queryTitle = searchParams.get('title');
  const queryDepartment = searchParams.get('department') || searchParams.get('dept');
  const queryPlant = searchParams.get('plant');
  const queryAmount = searchParams.get('amount');
  const queryCategory = searchParams.get('category');

  // Form State
  const [sourcePrData, setSourcePrData] = useState<any>(null);
  const [sourcePRId, setSourcePRId] = useState(querySourcePrId || 'PR-10001');
  const [sourceRequestId, setSourceRequestId] = useState(querySourceRequestId || 'REQ-1001');
  const [sapPrNumber, setSapPrNumber] = useState(querySapPrNumber || '45000123');

  const [title, setTitle] = useState(queryTitle ? `Sourcing RFQ: ${queryTitle}` : '');
  const [description, setDescription] = useState(
    queryTitle ? `Competitive strategic quotation request for ${queryTitle} under ${querySourceRequestId || 'PR'}.` : ''
  );
  const [category, setCategory] = useState(queryCategory || 'IT Hardware');
  const [priority, setPriority] = useState<'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'>('HIGH');
  const [procurementOwner] = useState('David Sterling');
  const [departmentName, setDepartmentName] = useState(queryDepartment || 'Information Technology');
  const [plantCode, setPlantCode] = useState(queryPlant || 'PLANT-1000');
  const [estimatedTotalAmount, setEstimatedTotalAmount] = useState<number>(Number(queryAmount) || 9000);

  const [paymentTerms, setPaymentTerms] = useState('Net 45 Days post GRN inspection');
  const [incoterms, setIncoterms] = useState('DDP (Delivered Duty Paid - Central Hub)');
  const [deliveryTerms, setDeliveryTerms] = useState('Door delivery to Receiving Bay');
  const [warrantyPeriod, setWarrantyPeriod] = useState('3 Years 24/7 Onsite OEM Premier Support');
  const [validityPeriod] = useState('60 Days from RFQ Closing Date');
  const [freightTerms] = useState('Supplier Account (Inclusive)');

  const [closingDate, setClosingDate] = useState('2026-09-15');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const targetPrId = querySourcePrId || querySourceRequestId;
    if (targetPrId) {
      mockPrManagementService.getPrById(targetPrId).then((pr) => {
        if (pr) {
          setSourcePrData(pr);
          setSourcePRId(pr.id);
          setSourceRequestId(pr.requestNumber);
          if (pr.sapDetails?.sapPrNumber) setSapPrNumber(pr.sapDetails.sapPrNumber);
          setTitle(`Sourcing RFQ: ${pr.title}`);
          setDescription(pr.justification || `Competitive strategic quotation request for ${pr.title}.`);
          if (pr.departmentName) setDepartmentName(pr.departmentName);
          if (pr.plantCode) setPlantCode(pr.plantCode);
          if (pr.totalAmount) setEstimatedTotalAmount(pr.totalAmount);
          if (pr.priority) setPriority(pr.priority);
          if (pr.items?.[0]?.category) setCategory(pr.items[0].category);
        }
      });
    }
  }, [querySourcePrId, querySourceRequestId]);

  const handleSubmit = async () => {
    if (!title || !description) {
      setError('Title and Description are required.');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const seq = (sourceRequestId || sourcePRId).replace(/[^0-9]/g, '') || String(Date.now()).slice(-4);

      const itemsToSubmit =
        sourcePrData?.items && sourcePrData.items.length > 0
          ? sourcePrData.items.map((it: any, idx: number) => ({
              id: `RFQ-ITEM-NEW-${idx + 1}`,
              itemNumber: it.itemNumber || (idx + 1) * 10,
              materialCode: it.materialCode || `MAT-${idx + 1}`,
              description: it.description || it.materialName || title,
              category: it.category || category,
              quantity: it.quantity || 1,
              uom: it.uom || 'EA',
              estimatedUnitPrice: it.unitPrice || it.estimatedUnitPrice || estimatedTotalAmount,
              totalEstimatedPrice: it.totalPrice || it.totalEstimatedPrice || estimatedTotalAmount,
              currency: it.currency || 'INR',
              plantCode: it.plantCode || plantCode,
              deliveryDate: it.deliveryDate || closingDate,
            }))
          : [
              {
                id: 'RFQ-ITEM-NEW-1',
                itemNumber: 10,
                materialCode: `MAT-${seq}`,
                description: title.replace('Sourcing RFQ: ', ''),
                category,
                quantity: 1,
                uom: 'EA',
                estimatedUnitPrice: estimatedTotalAmount,
                totalEstimatedPrice: estimatedTotalAmount,
                currency: 'INR',
                plantCode,
                deliveryDate: closingDate,
              },
            ];

      const suppliersToSubmit = [
        {
          id: `SUP-1-${seq}`,
          supplierId: 'VEND-101',
          supplierName: 'Dell Enterprise Solutions Ltd',
          category,
          contactEmail: 'bids@dell.enterprise',
          invitationStatus: 'RESPONDED' as const,
          invitedAt: new Date().toISOString(),
          respondedAt: new Date().toISOString(),
          quotationId: `quo-dyn-${seq}`,
          quotedAmount: estimatedTotalAmount,
          currency: sourcePrData?.currency || 'INR',
        },
        {
          id: `SUP-2-${seq}`,
          supplierId: 'VEND-102',
          supplierName: 'Lenovo Commercial Systems Ltd',
          category,
          contactEmail: 'procurement@lenovo.enterprise',
          invitationStatus: 'RESPONDED' as const,
          invitedAt: new Date().toISOString(),
          respondedAt: new Date().toISOString(),
          quotationId: `quo-alt-${seq}`,
          quotedAmount: Math.round(estimatedTotalAmount * 1.05),
          currency: sourcePrData?.currency || 'INR',
        },
      ];

      const created = await mockRfqManagementService.createRfq({
        title,
        description,
        category,
        sourceRequestId,
        sourcePRId,
        sapPrNumber,
        procurementOwner,
        departmentName,
        companyCode: '1000',
        plantCode,
        purchasingOrg: 'PO-GLOBAL',
        purchasingGroup: '001',
        priority,
        status: 'OPEN',
        estimatedTotalAmount,
        currency: sourcePrData?.currency || 'INR',
        issueDate: new Date().toISOString(),
        closingDate: `${closingDate}T17:00:00Z`,
        timezone: 'IST (UTC+05:30)',
        items: itemsToSubmit,
        suppliers: suppliersToSubmit,
        commercialTerms: {
          paymentTerms,
          incoterms,
          deliveryTerms,
          warrantyPeriod,
          validityPeriod,
          freightTerms,
        },
        lifecycle: [
          { key: 'CRT', label: '1. RFQ Draft Created', status: 'COMPLETED', timestamp: new Date().toISOString().split('T')[0] },
          { key: 'INV', label: '2. Select Suppliers', status: 'ACTIVE' },
          { key: 'RSP', label: '3. Awaiting Bids', status: 'PENDING' },
          { key: 'EVL', label: '4. Evaluation Pending', status: 'PENDING' },
        ],
        quotationIds: [],
      });

      navigate(`/procurement/rfq/${created.id}`);
    } catch (err: any) {
      setError(err.message || 'Failed to create RFQ Package');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 pb-24 font-poppins max-w-4xl mx-auto">
      {/* Top Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(ROUTES.RFQ.LIST)}
            className="h-9 rounded-xl border-border font-bold"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to RFQ List
          </Button>

          <h1 className="text-2xl font-extrabold tracking-tight text-foreground flex items-center gap-2">
            <Layers className="h-6 w-6 text-primary" /> Create RFQ Strategic Sourcing Package
          </h1>
        </div>
      </div>

      {/* Stepper Bar */}
      <div className="grid grid-cols-4 gap-2 text-xs font-bold">
        {[
          { num: 1, label: '1. Source PR' },
          { num: 2, label: '2. Sourcing Details' },
          { num: 3, label: '3. Commercial Terms' },
          { num: 4, label: '4. Review & Submit' },
        ].map((s) => (
          <div
            key={s.num}
            onClick={() => setStep(s.num as any)}
            className={`p-3 rounded-xl border text-center cursor-pointer transition-all ${
              step === s.num
                ? 'border-primary bg-primary text-white shadow-2xs'
                : step > s.num
                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700'
                : 'border-border bg-card text-foreground-secondary'
            }`}
          >
            {s.label}
          </div>
        ))}
      </div>

      {error && (
        <div className="p-3 rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-700 font-bold text-xs">
          {error}
        </div>
      )}

      {/* STEP 1: SOURCE PR */}
      {step === 1 && (
        <Card className="border-border rounded-2xl shadow-2xs p-6 space-y-4 text-xs">
          <div className="space-y-1">
            <label className="font-bold text-foreground block">Select Source Purchase Requisition *</label>
            <select
              value={sourcePRId}
              onChange={(e) => {
                const val = e.target.value;
                setSourcePRId(val);
                if (val === 'PR-10001') {
                  setSourceRequestId('REQ-1001');
                  setSapPrNumber('45000123');
                  setTitle('High-Performance Workstation Deployment (ThinkPad P1 Gen 6)');
                  setDescription('Strategic sourcing package for 10x Developer Workstations with 3-Year Onsite ProSupport.');
                } else {
                  setSourceRequestId('REQ-1002');
                  setSapPrNumber('45000124');
                  setTitle('HVAC Industrial Chiller Unit Replacement Sourcing Package 50-Ton');
                  setDescription('Turnkey procurement package for 50-Ton HVAC Chiller System for Noida Plant 102.');
                }
              }}
              className="w-full rounded-xl border border-border bg-background px-3 py-2.5 font-bold text-foreground"
            >
              <option value="PR-10001">PR-10001 (REQ-1001 / SAP PR 45000123) - ThinkPad Workstation Refresh</option>
              <option value="PR-10002">PR-10002 (REQ-1002 / SAP PR 45000124) - HVAC Chiller Unit Replacement</option>
            </select>
          </div>

          <div className="p-4 rounded-xl border border-primary/20 bg-primary/5 space-y-1">
            <span className="font-bold text-primary block">Source Lineage Context:</span>
            <p className="text-foreground-secondary">
              ProcureAI Request: <strong className="text-foreground">{sourceRequestId}</strong> • SAP PR Document:{' '}
              <strong className="text-purple-600 font-mono">{sapPrNumber}</strong>
            </p>
          </div>

          <div className="flex justify-end pt-4 border-t border-border">
            <Button onClick={() => setStep(2)} className="rounded-xl font-bold bg-primary text-white">
              Next: Sourcing Details →
            </Button>
          </div>
        </Card>
      )}

      {/* STEP 2: SOURCING DETAILS */}
      {step === 2 && (
        <Card className="border-border rounded-2xl shadow-2xs p-6 space-y-4 text-xs">
          <div className="space-y-1">
            <label className="font-bold text-foreground block">RFQ Package Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Strategic Workstation Sourcing Package"
              className="w-full rounded-xl border border-border bg-background px-3 py-2.5 font-bold text-foreground"
              required
            />
          </div>

          <div className="space-y-1">
            <label className="font-bold text-foreground block">Scope & Objective Description *</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Detailed sourcing objectives, required supplier qualifications, and technical scope..."
              className="w-full rounded-xl border border-border bg-background p-3 font-medium text-foreground"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-bold text-foreground block mb-1">Closing Date (IST) *</label>
              <input
                type="date"
                value={closingDate}
                onChange={(e) => setClosingDate(e.target.value)}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 font-mono font-bold text-foreground"
              />
            </div>

            <div>
              <label className="font-bold text-foreground block mb-1">Priority</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as any)}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 font-bold text-foreground"
              >
                <option value="LOW">LOW</option>
                <option value="MEDIUM">MEDIUM</option>
                <option value="HIGH">HIGH</option>
                <option value="CRITICAL">CRITICAL</option>
              </select>
            </div>
          </div>

          <div className="flex justify-between pt-4 border-t border-border">
            <Button variant="outline" onClick={() => setStep(1)} className="rounded-xl font-bold">
              ← Back
            </Button>
            <Button onClick={() => setStep(3)} className="rounded-xl font-bold bg-primary text-white">
              Next: Commercial Terms →
            </Button>
          </div>
        </Card>
      )}

      {/* STEP 3: COMMERCIAL TERMS */}
      {step === 3 && (
        <Card className="border-border rounded-2xl shadow-2xs p-6 space-y-4 text-xs">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-bold text-foreground block mb-1">Payment Terms *</label>
              <input
                type="text"
                value={paymentTerms}
                onChange={(e) => setPaymentTerms(e.target.value)}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 font-bold text-foreground"
              />
            </div>

            <div>
              <label className="font-bold text-foreground block mb-1">Incoterms *</label>
              <input
                type="text"
                value={incoterms}
                onChange={(e) => setIncoterms(e.target.value)}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 font-bold text-foreground"
              />
            </div>

            <div>
              <label className="font-bold text-foreground block mb-1">Delivery Terms *</label>
              <input
                type="text"
                value={deliveryTerms}
                onChange={(e) => setDeliveryTerms(e.target.value)}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 font-bold text-foreground"
              />
            </div>

            <div>
              <label className="font-bold text-foreground block mb-1">Warranty Requirements *</label>
              <input
                type="text"
                value={warrantyPeriod}
                onChange={(e) => setWarrantyPeriod(e.target.value)}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 font-bold text-foreground"
              />
            </div>
          </div>

          <div className="flex justify-between pt-4 border-t border-border">
            <Button variant="outline" onClick={() => setStep(2)} className="rounded-xl font-bold">
              ← Back
            </Button>
            <Button onClick={() => setStep(4)} className="rounded-xl font-bold bg-primary text-white">
              Next: Review & Submit →
            </Button>
          </div>
        </Card>
      )}

      {/* STEP 4: REVIEW & SUBMIT */}
      {step === 4 && (
        <Card className="border-border rounded-2xl shadow-2xs p-6 space-y-4 text-xs">
          <h3 className="font-bold text-sm text-foreground pb-2 border-b border-border">RFQ Sourcing Package Review</h3>

          <div className="space-y-2">
            <p>Source PR: <strong className="text-foreground font-mono">{sourcePRId}</strong> (SAP: {sapPrNumber})</p>
            <p>Title: <strong className="text-foreground">{title}</strong></p>
            <p>Scope: <span className="text-foreground-secondary">{description}</span></p>
            <p>Closing Date: <strong className="text-primary font-mono">{closingDate} (IST)</strong></p>
          </div>

          <div className="flex justify-between pt-4 border-t border-border">
            <Button variant="outline" onClick={() => setStep(3)} className="rounded-xl font-bold">
              ← Back to Terms
            </Button>
            <Button
              disabled={isSubmitting}
              onClick={handleSubmit}
              className="rounded-xl font-bold bg-primary text-white shadow-2xs"
            >
              {isSubmitting ? 'Creating RFQ Package...' : 'Create RFQ Package'}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};
